import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Reply, MailOpen, RefreshCw } from "lucide-react";
import { contactsApi } from "./api";
import type { ContactMessage } from "./types";
import { StatusBadge } from "../applications/shared/StatusBadge";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { ApplicationDrawer } from "../applications/shared/ApplicationDrawer";
import { Section, Field } from "../applications/shared/InfoBlock";
import { formatDate, formatDateTime } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { useDebounce } from "@/lib/utils/useDebounce";

const STATUS_OPTIONS = [
  { value: "new", label: "Nuevo" },
  { value: "read", label: "Leído" },
  { value: "replied", label: "Respondido" },
  { value: "archived", label: "Archivado" },
];

const TOPIC_LABELS: Record<string, string> = {
  general: "General",
  sales: "Ventas",
  press: "Prensa",
  partnerships: "Partnerships",
  technical: "Soporte técnico",
  other: "Otro",
};

const PAGE_SIZE = 25;

export function ContactScreen() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, status]);

  const filters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      status: status || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, status, page],
  );

  const listQuery = useQuery({
    queryKey: ["communications", "contacts", filters],
    queryFn: () => contactsApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ["communications", "contacts", "detail", selectedId],
    queryFn: () => contactsApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Comunicaciones · Contacto</p>
          <h1 className="nx-h1">Mensajes de contacto</h1>
          <p className="text-sm text-fg-muted mt-1">
            Mensajes públicos desde <code>/contacto</code>. Para preguntas
            generales, ventas, prensa, partnerships o soporte técnico.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nombre, email, asunto o contenido…"
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando mensajes…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<ContactMessage>
          rows={listQuery.data?.contacts ?? []}
          columns={columns}
          page={listQuery.data?.page ?? 1}
          pageSize={listQuery.data?.pageSize ?? PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
        />
      )}

      <ContactDrawer
        selectedId={selectedId}
        data={detailQuery.data ?? null}
        loading={detailQuery.isLoading && selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

const columns: Column<ContactMessage>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "subject",
    header: "Asunto",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.subject}</div>
        <div className="text-2xs text-fg-subtle line-clamp-1 max-w-md">{r.message}</div>
      </div>
    ),
  },
  {
    key: "from",
    header: "De",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.fullName}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.email}</div>
      </div>
    ),
  },
  {
    key: "topic",
    header: "Tema",
    cell: (r) => (
      <span className="text-fg-muted text-xs">{TOPIC_LABELS[r.topic] ?? r.topic}</span>
    ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => <StatusBadge status={r.status} />,
    width: "min",
  },
  {
    key: "createdAt",
    header: "Recibido",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.createdAt)}</span>,
    width: "min",
    align: "right",
  },
];

function ContactDrawer({
  selectedId,
  data,
  loading,
  onClose,
}: {
  selectedId: number | null;
  data: ContactMessage | null;
  loading: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["communications", "contacts"] });

  const updateNotes = useMutation({
    mutationFn: (notes: string | null) =>
      contactsApi.update(selectedId!, { internalNotes: notes }),
    onSuccess: () => {
      push({ kind: "success", message: "Notas guardadas." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudieron guardar las notas." }),
  });

  const updateStatus = useMutation({
    mutationFn: (newStatus: string) =>
      contactsApi.update(selectedId!, { status: newStatus }),
    onSuccess: () => {
      push({ kind: "success", message: "Estado actualizado." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo actualizar." }),
  });

  return (
    <ApplicationDrawer<ContactMessage>
      open={selectedId !== null}
      data={data}
      loading={loading}
      title={data?.subject ?? "Mensaje"}
      subtitle={data ? `#${data.id} · ${formatDateTime(data.createdAt)}` : undefined}
      onClose={onClose}
      onSaveNotes={(notes) => updateNotes.mutateAsync(notes).then(() => undefined)}
      renderBody={(d) => (
        <>
          <div className="flex items-center gap-2"><StatusBadge status={d.status} /></div>

          <Section title="Remitente">
            <Field label="Nombre" value={d.fullName} wide />
            <Field
              label="Email"
              value={
                <a className="text-accent hover:underline" href={`mailto:${d.email}`}>
                  {d.email}
                </a>
              }
              wide
            />
            <Field label="Tema" value={TOPIC_LABELS[d.topic] ?? d.topic} />
          </Section>

          <Section title="Mensaje">
            <Field label="Asunto" value={d.subject} wide />
            <Field
              label="Cuerpo"
              value={<p className="whitespace-pre-wrap text-fg">{d.message}</p>}
              wide
            />
          </Section>

          <Section title="Tracking">
            <Field label="IP" value={d.ipAddress ?? "—"} />
            <Field label="Última actualización" value={formatDateTime(d.updatedAt)} />
            <Field
              label="User agent"
              value={<span className="text-2xs break-all">{d.userAgent ?? "—"}</span>}
              wide
            />
          </Section>
        </>
      )}
      renderActions={(d) => (
        <>
          {d.status === "new" && (
            <button
              onClick={() => updateStatus.mutate("read")}
              disabled={updateStatus.isPending}
              className="nx-btn-secondary"
            >
              <MailOpen size={14} /> Marcar leído
            </button>
          )}
          {(d.status === "new" || d.status === "read") && (
            <button
              onClick={() => updateStatus.mutate("replied")}
              disabled={updateStatus.isPending}
              className="nx-btn-secondary"
            >
              <Reply size={14} /> Marcar respondido
            </button>
          )}
          {d.status !== "archived" && (
            <button
              onClick={() => updateStatus.mutate("archived")}
              disabled={updateStatus.isPending}
              className="nx-btn-secondary"
            >
              <Archive size={14} /> Archivar
            </button>
          )}
          {d.status === "archived" && (
            <button
              onClick={() => updateStatus.mutate("read")}
              disabled={updateStatus.isPending}
              className="nx-btn-secondary"
            >
              Reabrir
            </button>
          )}
          <a
            href={`mailto:${d.email}?subject=Re: ${encodeURIComponent(d.subject)}`}
            className="nx-btn-primary"
          >
            <Reply size={14} /> Responder por email
          </a>
        </>
      )}
    />
  );
}
